/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { createContext, useContext, type PropsWithChildren } from 'react';
import type { ExtendOffer } from '@/lib/api/extend.server';

/**
 * INT-02 — Carries the deferred `POST /offers/cart` response down to per-row
 * cart toggles via context (avoids prop-drilling through `ProductItemsList`
 * and its render-prop machinery).
 *
 * Shape: `Promise<Record<productId, ExtendOffer>>`. A productId missing from
 * the map means the cartridge returned no entry — treat as ineligible.
 *
 * The cart route resolves the promise lazily; the toggle component reads it
 * via `use(promise)` inside its own `<Suspense>` boundary so the rest of the
 * cart row paints immediately. When the loader can't fetch (transport or
 * configuration failure) the promise resolves to `{}` — every toggle hides
 * gracefully.
 */
type ExtendCartOffersValue = Promise<Record<string, ExtendOffer>>;

const ExtendCartOffersContext = createContext<ExtendCartOffersValue | null>(null);

interface ExtendCartOffersProviderProps {
    offersPromise: ExtendCartOffersValue;
}

export function ExtendCartOffersProvider({
    children,
    offersPromise,
}: PropsWithChildren<ExtendCartOffersProviderProps>) {
    return <ExtendCartOffersContext.Provider value={offersPromise}>{children}</ExtendCartOffersContext.Provider>;
}

/**
 * Returns the offers promise, or `null` if no provider is mounted (e.g.
 * legacy/mini-cart contexts). Consumers should render no checkbox in the
 * null case.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useExtendCartOffersPromise(): ExtendCartOffersValue | null {
    return useContext(ExtendCartOffersContext);
}
